import { Action } from 'redux';
import { ConceptState, ConceptUnloadState } from '../states/ConceptState';
import { Concept } from '../../models/Entities/Contracts/Concept';
import { ConceptSponsor } from '../../models/Entities/Contracts/ConceptSponsor';

export default function ConceptReducer(
  state: ConceptState | undefined,
  action: Action
): ConceptState {
  const currentState = state ?? ConceptUnloadState;
  const a: any = action;
  const normalizeSponsorId = (value?: string): string => (value || '').toLowerCase();

  const mergeSponsorIntoConcept = (concept: Concept): Concept => {
    const currentSponsors = concept.conceptSponsors || [];
    const updatedSponsors = [
      ...currentSponsors.filter((item) => item.id !== a.conceptSponsor.id),
      a.conceptSponsor
    ];

    return {
      ...concept,
      conceptSponsors: updatedSponsors
    };
  };

  const replaceConceptSponsorsForSponsor = (
    concept: Concept,
    sponsorId: string,
    nextConceptSponsors: ConceptSponsor[]
  ): Concept => {
    const normalizedSponsorId = normalizeSponsorId(sponsorId);
    const currentSponsors = concept.conceptSponsors || [];
    const conceptSponsorsForCurrentConcept = nextConceptSponsors.filter(
      (item) => item.conceptId === concept.id
    );

    return {
      ...concept,
      conceptSponsors: [
        ...currentSponsors.filter(
          (item) => normalizeSponsorId(item.sponsorId) !== normalizedSponsorId
        ),
        ...conceptSponsorsForCurrentConcept
      ]
    };
  };

  switch (a.type) {
    case 'CONCEPT_GET_ALL_REQUEST_ACTION':
      return {
        ...currentState,
        isLoadingAll: true,
        successLoadingAll: undefined,
        failOnLoadingAll: false,
        error: undefined
      };
    case 'CONCEPT_GET_ALL_RECEIVE_ACTION':
      return {
        ...currentState,
        isLoadingAll: false,
        successLoadingAll: true,
        failOnLoadingAll: false,
        list: a.concepts,
        error: undefined
      };
    case 'CONCEPT_GET_ALL_FAIL_ACTION':
      return {
        ...currentState,
        isLoadingAll: false,
        successLoadingAll: false,
        failOnLoadingAll: true,
        error: a.error
      };

    case 'CONCEPT_GET_BY_ID_REQUEST_ACTION':
      return {
        ...currentState,
        isLoadingOne: true,
        successLoadingOne: undefined,
        failOnLoadingOne: false,
        error: undefined
      };
    case 'CONCEPT_GET_BY_ID_RECEIVE_ACTION': {
      const existing = currentState.list.find((c: Concept) => c.id === a.concept.id);
      const updated = existing ? { ...existing, ...a.concept } : a.concept;
      return {
        ...currentState,
        isLoadingOne: false,
        successLoadingOne: true,
        failOnLoadingOne: false,
        selected: updated,
        list: existing
          ? currentState.list.map((c: Concept) => (c.id === updated.id ? updated : c))
          : [...currentState.list, updated],
        error: undefined
      };
    }
    case 'CONCEPT_GET_BY_ID_FAIL_ACTION':
      return {
        ...currentState,
        isLoadingOne: false,
        successLoadingOne: false,
        failOnLoadingOne: true,
        error: a.error
      };

    case 'CONCEPT_ADD_REQUEST_ACTION':
      return {
        ...currentState,
        isAddingNewOne: true,
        successAddingNewOne: undefined,
        failOnAddingNewOne: false,
        error: undefined
      };
    case 'CONCEPT_ADD_RECEIVE_ACTION':
      return {
        ...currentState,
        isAddingNewOne: false,
        successAddingNewOne: true,
        failOnAddingNewOne: false,
        list: [...currentState.list, a.concept],
        error: undefined
      };
    case 'CONCEPT_ADD_FAIL_ACTION':
      return {
        ...currentState,
        isAddingNewOne: false,
        successAddingNewOne: false,
        failOnAddingNewOne: true,
        error: a.error
      };

    case 'CONCEPT_UPDATE_REQUEST_ACTION':
      return {
        ...currentState,
        isUpdatingOne: true,
        successUpdatingOne: undefined,
        failOnUpdatingOne: false,
        error: undefined
      };
    case 'CONCEPT_UPDATE_RECEIVE_ACTION':
      return {
        ...currentState,
        isUpdatingOne: false,
        successUpdatingOne: true,
        failOnUpdatingOne: false,
        list: currentState.list.map((c: Concept) =>
          c.id === a.concept.id ? { ...c, ...a.concept } : c
        ),
        selected:
          currentState.selected?.id === a.concept.id
            ? { ...currentState.selected, ...a.concept }
            : currentState.selected,
        error: undefined
      };
    case 'CONCEPT_UPDATE_FAIL_ACTION':
      return {
        ...currentState,
        isUpdatingOne: false,
        successUpdatingOne: false,
        failOnUpdatingOne: true,
        error: a.error
      };

    case 'CONCEPT_DELETE_REQUEST_ACTION':
      return {
        ...currentState,
        isDeletingOne: true,
        successDeletingOne: undefined,
        failOnDeletingOne: false,
        error: undefined
      };
    case 'CONCEPT_DELETE_RECEIVE_ACTION':
      return {
        ...currentState,
        isDeletingOne: false,
        successDeletingOne: true,
        failOnDeletingOne: false,
        list: currentState.list.filter((c: Concept) => c.id !== a.conceptId),
        selected: currentState.selected?.id === a.conceptId ? undefined : currentState.selected,
        error: undefined
      };
    case 'CONCEPT_DELETE_FAIL_ACTION':
      return {
        ...currentState,
        isDeletingOne: false,
        successDeletingOne: false,
        failOnDeletingOne: true,
        error: a.error
      };

    case 'CONCEPTSPONSOR_GET_ALL_BY_SPONSORID_REQUEST_ACTION':
      return {
        ...currentState,
        isLoadingConceptSponsors: true
      };

    case 'CONCEPTSPONSOR_GET_ALL_BY_SPONSORID_FAIL_ACTION':
      return {
        ...currentState,
        isLoadingConceptSponsors: false
      };

    case 'CONCEPTSPONSOR_GET_ALL_BY_SPONSORID_RECEIVE_ACTION': {
      const receivedConceptSponsors = a.conceptSponsors || [];

      return {
        ...currentState,
        isLoadingConceptSponsors: false,
        list: currentState.list.map((c: Concept) =>
          replaceConceptSponsorsForSponsor(c, a.sponsorId, receivedConceptSponsors)
        ),
        selected: currentState.selected
          ? replaceConceptSponsorsForSponsor(currentState.selected, a.sponsorId, receivedConceptSponsors)
          : currentState.selected
      };
    }

    case 'CONCEPTSPONSOR_GET_BY_ID_RECEIVE_ACTION':
    case 'CONCEPTSPONSOR_ADD_RECEIVE_ACTION':
    case 'CONCEPTSPONSOR_UPDATE_RECEIVE_ACTION':
      return {
        ...currentState,
        list: currentState.list.map((c: Concept) =>
          c.id === a.conceptSponsor.conceptId ? mergeSponsorIntoConcept(c) : c
        ),
        selected:
          currentState.selected && currentState.selected.id === a.conceptSponsor.conceptId
            ? mergeSponsorIntoConcept(currentState.selected)
            : currentState.selected
      };

    case 'CONCEPTSPONSOR_DELETE_RECEIVE_ACTION':
      return {
        ...currentState,
        list: currentState.list.map((c: Concept) => ({
          ...c,
          conceptSponsors: (c.conceptSponsors || []).filter((item) => item.id !== a.conceptSponsorId)
        })),
        selected: currentState.selected
          ? {
              ...currentState.selected,
              conceptSponsors: (currentState.selected.conceptSponsors || []).filter(
                (item) => item.id !== a.conceptSponsorId
              )
            }
          : currentState.selected
      };

    default:
      return currentState;
  }
}
